import type { Character } from "../types/character";
import { ALL_CHARACTERS } from "../types/character";
import type {
  HorrorRaceState,
  RoundDrawRecord,
  RoundTurnDraw,
} from "../types/game-state";
import { getCurrentRaceDrawHistory } from "./draw-history";

/**
 * M1 — one-line table-row summary the coin-memory sheet renders directly.
 * `counts` is the per-character tally of revealed draws (forfeit draws are
 * excluded). `unknownCount` is forfeit-tainted: 3 × number of forfeited turns
 * in this record. `isComplete` ⇔ no forfeit AND all expected turns drawn.
 */
export interface RoundCountSummary {
  readonly raceNumber: number;
  readonly roundNumber: number;
  readonly counts: Readonly<Record<Character, number>>;
  readonly unknownCount: number;
  readonly isComplete: boolean;
  readonly isLive: boolean;
}

function zeroCounts(): Record<Character, number> {
  const out: Record<Character, number> = {
    VAMPIRE: 0,
    JIANGSHI: 0,
    GUMIHO: 0,
    MUMMY: 0,
    ZOMBIE: 0,
  };
  return out;
}

function tally(draws: readonly RoundTurnDraw[]): {
  readonly counts: Readonly<Record<Character, number>>;
  readonly unknownCount: number;
} {
  const counts = zeroCounts();
  let unknownCount = 0;
  for (const draw of draws) {
    if (draw.coins === null) {
      unknownCount += 3;
      continue;
    }
    for (const coin of draw.coins) {
      counts[coin] += 1;
    }
  }
  return { counts, unknownCount };
}

/**
 * Derive the per-row view for one record. `isLive` is the caller's
 * responsibility (it depends on whether this record corresponds to the
 * state's current (race, round) tuple).
 */
export function summarizeRound(
  record: RoundDrawRecord,
  opts: { readonly isLive: boolean },
): RoundCountSummary {
  const { counts, unknownCount } = tally(record.draws);
  return {
    raceNumber: record.raceNumber,
    roundNumber: record.roundNumber,
    counts,
    unknownCount,
    isComplete: record.isComplete,
    isLive: opts.isLive,
  };
}

/**
 * All summaries for the sheet body — current race only, sorted desc by
 * roundNumber. Prior-race records are dropped (the engine resets the history
 * on advance_race, but this is defensive for any caller that constructs
 * state by hand). Marks the current (race, round) row with isLive=true.
 *
 * Used by `<CoinMemorySheet>` body to render rows.
 */
export function summarizeAll(
  state: HorrorRaceState,
): readonly RoundCountSummary[] {
  const history = getCurrentRaceDrawHistory(state);
  const currentRace = state.raceNumber;
  const currentRound = state.round;
  const sorted = [...history]
    .filter((r) => r.raceNumber === currentRace)
    .sort((a, b) => b.roundNumber - a.roundNumber);
  return sorted.map((r) =>
    summarizeRound(r, {
      isLive: r.raceNumber === currentRace && r.roundNumber === currentRound,
    }),
  );
}

/**
 * The "★ exact" top row of the sheet — the latest complete round in the
 * current race, or null if no complete round exists yet. All completes in a
 * race have identical counts (pool reshuffles to the same composition each
 * round + finished characters are removed only at race end), so "latest" is
 * arbitrary but stable.
 *
 * Returns `isLive: false` always — the summary row is an aggregate, not a
 * live mirror of any one round.
 */
export function selectExactSummary(
  state: HorrorRaceState,
): RoundCountSummary | null {
  const history = getCurrentRaceDrawHistory(state);
  let latestComplete: RoundDrawRecord | null = null;
  for (const r of history) {
    if (r.raceNumber !== state.raceNumber) continue;
    if (!r.isComplete) continue;
    if (latestComplete === null || r.roundNumber > latestComplete.roundNumber) {
      latestComplete = r;
    }
  }
  if (latestComplete === null) return null;
  return summarizeRound(latestComplete, { isLive: false });
}

export type { Character };
// Re-exported for callers that want to iterate columns in render order.
export const SUMMARY_CHARACTER_ORDER = ALL_CHARACTERS;
